import moment from 'moment';

/**
 * Conflict Detection Utility for Scheduler
 *
 * Provides frontend validation to detect scheduling conflicts before saving.
 * Since the API doesn't validate conflicts, this prevents guard double-booking
 * and helps users identify potential issues.
 */

/**
 * Check if two time ranges overlap
 * @param {Date|string} aStart - Start time of first range
 * @param {Date|string} aEnd - End time of first range
 * @param {Date|string} bStart - Start time of second range
 * @param {Date|string} bEnd - End time of second range
 * @returns {boolean} True if the ranges overlap
 */
export const isOverlapping = (aStart, aEnd, bStart, bEnd) => {
  const a1 = moment(aStart);
  const a2 = moment(aEnd);
  const b1 = moment(bStart);
  const b2 = moment(bEnd);

  // Two ranges overlap if one starts before the other ends AND ends after the other starts
  return a1.isBefore(b2) && a2.isAfter(b1);
};

/**
 * Detect scheduling conflicts for a patrol
 * @param {Object} newPatrol - The patrol being created/edited
 * @param {Array} existingPatrols - All existing patrols to check against
 * @returns {Array} Array of conflict objects describing each conflict found
 */
export const detectConflicts = (newPatrol, existingPatrols) => {
  const conflicts = [];

  if (!newPatrol || !existingPatrols || existingPatrols.length === 0) {
    return conflicts;
  }

  const newStart = moment(newPatrol.StartTime);
  const newEnd = moment(newPatrol.EndTime);
  const newPatrolId = newPatrol.ScheduleAssignmentId;
  const newContactId = String(newPatrol.ContactId);

  // Filter to only check patrols for the same guard (excluding the patrol being edited)
  const guardPatrols = existingPatrols.filter((p) => {
    const isOwnPatrol = newPatrolId && p.ScheduleAssignmentId === newPatrolId;
    return String(p.ContactId) === newContactId && !isOwnPatrol;
  });

  for (const existing of guardPatrols) {
    const existingStart = moment(existing.StartTime);
    const existingEnd = moment(existing.EndTime);

    if (isOverlapping(newStart, newEnd, existingStart, existingEnd)) {
      conflicts.push({
        type: 'GUARD_DOUBLE_BOOKING',
        message: `${newPatrol.ContactName} is already scheduled at "${existing.AddressName}" from ${existingStart.format('h:mm A')} to ${existingEnd.format('h:mm A')}`,
        conflictingPatrol: existing,
        overlapStart: moment.max(newStart, existingStart),
        overlapEnd: moment.min(newEnd, existingEnd),
      });
    }
  }

  return conflicts;
};

/**
 * Check for conflicts across multiple patrols (for bulk operations like duplicate week)
 * @param {Array} newPatrols - Array of new patrols being created
 * @param {Array} existingPatrols - All existing patrols to check against
 * @returns {Array} Array of conflict objects
 */
export const detectBulkConflicts = (newPatrols, existingPatrols) => {
  const allConflicts = [];

  if (!newPatrols || newPatrols.length === 0) {
    return allConflicts;
  }

  for (const newPatrol of newPatrols) {
    const conflicts = detectConflicts(newPatrol, existingPatrols);
    allConflicts.push(...conflicts);
  }

  // Also check for conflicts within the new patrols themselves
  for (let i = 0; i < newPatrols.length; i++) {
    for (let j = i + 1; j < newPatrols.length; j++) {
      const patrolA = newPatrols[i];
      const patrolB = newPatrols[j];

      // Only check if same guard
      if (String(patrolA.ContactId) !== String(patrolB.ContactId)) {
        continue;
      }

      if (isOverlapping(patrolA.StartTime, patrolA.EndTime, patrolB.StartTime, patrolB.EndTime)) {
        allConflicts.push({
          type: 'INTERNAL_CONFLICT',
          message: `Two new patrols for ${patrolA.ContactName} overlap: "${patrolA.AddressName}" and "${patrolB.AddressName}"`,
          conflictingPatrol: patrolB,
          relatedPatrol: patrolA,
        });
      }
    }
  }

  return allConflicts;
};

/**
 * Format conflicts for display in a dialog
 * @param {Array} conflicts - Array of conflict objects
 * @returns {Object} Formatted conflict summary
 */
export const formatConflictSummary = (conflicts) => {
  if (!conflicts || conflicts.length === 0) {
    return null;
  }

  const guardConflicts = conflicts.filter((c) => c.type === 'GUARD_DOUBLE_BOOKING');
  const internalConflicts = conflicts.filter((c) => c.type === 'INTERNAL_CONFLICT');

  return {
    totalCount: conflicts.length,
    guardConflictCount: guardConflicts.length,
    internalConflictCount: internalConflicts.length,
    conflicts: conflicts,
    summary:
      conflicts.length === 1
        ? conflicts[0].message
        : `${conflicts.length} scheduling conflicts detected`,
  };
};

/**
 * Parse overnight patrol times
 * If end time is before start time (e.g., 10pm - 2am), adjusts end time to next day
 * @param {Object} patrol - Patrol with StartTime and EndTime
 * @returns {Object} Patrol with corrected times
 */
export const parseOvernightPatrol = (patrol) => {
  if (!patrol || !patrol.StartTime || !patrol.EndTime) {
    return patrol;
  }

  const startTime = moment(patrol.StartTime);
  const endTime = moment(patrol.EndTime);

  // If end time is before or equal to start time, it's an overnight patrol
  // Add 1 day to the end time
  if (endTime.isSameOrBefore(startTime)) {
    return {
      ...patrol,
      EndTime: endTime.add(1, 'day').toDate(),
      isOvernight: true,
    };
  }

  return patrol;
};

/**
 * Parse an array of patrols, handling overnight shifts
 * @param {Array} patrols - Array of patrol objects
 * @returns {Array} Patrols with corrected overnight times
 */
export const parseOvernightPatrols = (patrols) => {
  if (!patrols || patrols.length === 0) {
    return patrols;
  }

  return patrols.map(parseOvernightPatrol);
};

// Note: All functions are exported as named exports above.
// Import specific functions: import { detectConflicts, isOverlapping } from 'utils/conflictDetection';
